import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AssistantProposedTask } from 'src/app/services/assistant-chat/assistant-chat.service';
import { AssistantPanelService } from 'src/app/services/assistant-chat/assistant-panel.service';
import { AssistantPendingTasksService } from 'src/app/services/assistant-chat/assistant-pending-tasks.service';

/**
 * Contenedor del asistente conversacional.
 *
 * Vive en AppComponent para persistir entre rutas. La burbuja flotante ocupa el
 * borde inferior derecho: esa esquina queda libre porque el chat de Zendesk
 * sólo se carga para visitantes sin sesión (ver ZendeskService).
 */
@Component({
  selector: 'app-chat-launcher',
  templateUrl: './chat-launcher.component.html',
  styleUrls: ['./chat-launcher.component.sass'],
})
export class ChatLauncherComponent implements OnInit, OnDestroy {
  /** El contenedor decide si el asistente está disponible para este usuario. */
  @Input() enabled = false;

  open = false;

  private unsubscribe = new Subject<void>();

  constructor(
    private router: Router,
    private pendingTasks: AssistantPendingTasksService,
    private panel: AssistantPanelService,
  ) {}

  ngOnInit(): void {
    this.panel.open$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((open: boolean) => (this.open = open));
  }

  /** Abre o cierra el panel desde la burbuja flotante. */
  toggle(): void {
    this.panel.toggle();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  onOpenChange(open: boolean): void {
    if (open) {
      this.panel.open();
    } else {
      this.panel.close();
    }
  }

  /**
   * Tareas confirmadas por el usuario.
   *
   * El chat se usa desde cualquier pantalla, pero sólo el cómputo sabe
   * aplicarlas: se dejan en el buzón y, si hace falta, se navega al cómputo
   * personalizado, que las consume al terminar de cargar.
   */
  onApplyTasks(tasks: AssistantProposedTask[]): void {
    if (!tasks?.length) {
      return;
    }

    this.pendingTasks.set(tasks);

    if (this.isOnCustomCompute()) {
      // Ya está en el cómputo: se aplican en el acto.
      window.dispatchEvent(new CustomEvent('sismat:assistant-apply-tasks'));
      return;
    }

    // En cualquier otra pantalla hay que llevarlo al cómputo personalizado.
    this.panel.close();
    this.router.navigate(['/computo']);
  }

  /**
   * El cómputo personalizado es /computo a secas. Las variantes con id
   * (pre-establecido, o un cómputo guardado) no son el destino correcto para
   * una propuesta suelta del asistente.
   */
  private isOnCustomCompute(): boolean {
    const url = this.router.url.split('?')[0].replace(/\/$/, '');
    return url === '/computo';
  }
}
